import { Claims, Merchant } from '@database/types';

/** The resolution methods a store can switch on or off. */
export enum ResolutionMethod {
  REFUND = 'refund',
  RESHIP = 'reship',
  STORE_CREDIT = 'storeCredit',
  DRAFT_ORDER = 'draftOrder',
}

export type ResolutionOptions = Record<ResolutionMethod, boolean>;

/** The claim category as its wire value, so Prisma and typegen enums both index the map. */
export type ResolutionClaimCategory = `${Claims.ClaimCategory}`;

export type ResolutionOptionsByCategory = Record<ResolutionClaimCategory, ResolutionOptions>;

export interface StoreResolutionToggles {
  capabilities?: Partial<Pick<Merchant.Capability, 'credit' | 'refund' | 'reship' | 'draftOrder'>> | null;
  warrantySettings?: Partial<
    Pick<Merchant.WarrantySettings, 'refundEnabled' | 'reshipEnabled' | 'storeCreditEnabled'>
  > | null;
  generalSettings?: Partial<Pick<Merchant.GeneralSetting, 'storeCreditType'>> | null;
}

/** Which method each resolution type delivers; deny and close types, CUSTOM and RETURN map to none. */
const RESOLUTION_TYPE_METHODS: Partial<Record<string, ResolutionMethod>> = {
  [Claims.ResolutionType.REFUND]: ResolutionMethod.REFUND,
  [Claims.ResolutionType.FLAT_RATE]: ResolutionMethod.REFUND,
  [Claims.ResolutionType.STORE_CREDIT]: ResolutionMethod.STORE_CREDIT,
  [Claims.ResolutionType.CREDIT]: ResolutionMethod.STORE_CREDIT,
  [Claims.ResolutionType.FLAT_RATE_STORE_CREDIT]: ResolutionMethod.STORE_CREDIT,
  [Claims.ResolutionType.RESHIP]: ResolutionMethod.RESHIP,
  [Claims.ResolutionType.REPLACEMENT]: ResolutionMethod.RESHIP,
  [Claims.ResolutionType.EXCHANGE]: ResolutionMethod.RESHIP,
  [Claims.ResolutionType.DRAFT_ORDER]: ResolutionMethod.DRAFT_ORDER,
};

export function resolutionMethodFor(resolutionType: string | null | undefined): ResolutionMethod | null {
  if (!resolutionType) return null;

  return RESOLUTION_TYPE_METHODS[resolutionType] ?? null;
}

/**
 * The store-wide capability switches apply to every claim; a warranty claim is narrowed further by
 * the store's warranty settings. Defaults mirror the resolution UIs: an unset switch is on, except
 * draft orders, which a store opts into, and store credit, which also needs a store credit type to issue.
 */
export function resolutionOptionsForStore(
  store: StoreResolutionToggles | null | undefined,
): ResolutionOptionsByCategory {
  const capabilities = store?.capabilities;
  const warranty = store?.warrantySettings;
  const hasStoreCreditType = !!store?.generalSettings?.storeCreditType;

  const shipping: ResolutionOptions = {
    [ResolutionMethod.REFUND]: capabilities?.refund ?? true,
    [ResolutionMethod.RESHIP]: capabilities?.reship ?? true,
    [ResolutionMethod.STORE_CREDIT]: (capabilities?.credit ?? true) && hasStoreCreditType,
    [ResolutionMethod.DRAFT_ORDER]: capabilities?.draftOrder ?? false,
  };

  return {
    [Claims.ClaimCategory.SHIPPING]: shipping,
    [Claims.ClaimCategory.WARRANTY]: {
      [ResolutionMethod.REFUND]: shipping.refund && (warranty?.refundEnabled ?? true),
      [ResolutionMethod.RESHIP]: shipping.reship && (warranty?.reshipEnabled ?? true),
      [ResolutionMethod.STORE_CREDIT]: shipping.storeCredit && (warranty?.storeCreditEnabled ?? true),
      [ResolutionMethod.DRAFT_ORDER]: shipping.draftOrder,
    },
  };
}

/** The methods a set of options leaves switched on. */
export function enabledResolutionMethods(options: ResolutionOptions): ResolutionMethod[] {
  return Object.values(ResolutionMethod).filter(method => options[method]);
}

type ResolveItemLike = { resolution_final?: unknown; resolutionFinal?: unknown } | null | undefined;
type ResolveIntentLike =
  | { instrument?: unknown; lines?: Array<{ instrument?: unknown } | null | undefined> }
  | null
  | undefined;

/**
 * Every resolution type a resolve_claim payload would deliver: the top-level type, each filed and
 * added item's final type, the priced intent's instruments, and the order-level flat refund and
 * courtesy credit, which execution writes as FLAT_RATE and STORE_CREDIT lines.
 */
export function resolutionTypesInResolveInput(input: Record<string, unknown>): string[] {
  const types = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value === 'string' && value) types.add(value);
  };

  add(input.resolution_type ?? input.resolutionType);
  const claimItems = (input.claim_items ?? []) as ResolveItemLike[];
  const additionalItems = (input.additional_claim_items ?? []) as ResolveItemLike[];
  for (const item of [...claimItems, ...additionalItems]) {
    add(item?.resolution_final ?? item?.resolutionFinal);
  }
  const intent = input.intent as ResolveIntentLike;
  add(intent?.instrument);
  for (const line of intent?.lines ?? []) add(line?.instrument);
  if (input.flat_rate_refund != null) add(Claims.ResolutionType.FLAT_RATE);
  if (input.courtesy_store_credit != null) add(Claims.ResolutionType.STORE_CREDIT);

  return [...types];
}

/** The resolution types whose method the options switch off, in payload order. */
export function disallowedResolutionTypes(options: ResolutionOptions, resolutionTypes: string[]): string[] {
  return resolutionTypes.filter(type => {
    const method = resolutionMethodFor(type);

    return method !== null && !options[method];
  });
}

/** A recent note from the brand's own team on a claim, and the switched-off methods it names. */
export interface BrandApproval {
  noteId: string;
  methods: ResolutionMethod[];
}

// A weak guard only: the model decides what a note approves, and this confirms the note names that method at all.
const METHOD_WORDS: Record<ResolutionMethod, RegExp> = {
  [ResolutionMethod.REFUND]: /\brefund/i,
  [ResolutionMethod.RESHIP]: /\b(?:reship|resend|replace|replacement)/i,
  [ResolutionMethod.STORE_CREDIT]: /\bcredit\b/i,
  [ResolutionMethod.DRAFT_ORDER]: /\bdraft order/i,
};

/** The resolution methods a note's text names. */
export function methodsNamedIn(content: string | null | undefined): ResolutionMethod[] {
  if (!content) return [];

  return Object.values(ResolutionMethod).filter(method => METHOD_WORDS[method].test(content));
}

/** The options with every method the cited brand approval names switched back on; unchanged without a match. */
export function withBrandApproval(
  options: ResolutionOptions,
  approvals: BrandApproval[] | undefined,
  noteId: unknown,
): ResolutionOptions {
  const approval = typeof noteId === 'string' ? approvals?.find(candidate => candidate.noteId === noteId) : undefined;
  if (!approval) return options;

  return approval.methods.reduce((unlocked, method) => ({ ...unlocked, [method]: true }), options);
}
